/**
 * Store link correction
 *
 * Content (metaobjects, rich text, templates) is shared between BODi stores and environments,
 * so it can contain absolute links that send visitors to the wrong store. Links inside <main>
 * are pointed at the store the visitor is on.
 *
 * Stores (host -> region):
 *   www.[env.]bodi.com      us   (bodi.com, shop., shop-us. are treated as aliases)
 *   shop-ca.[env.]bodi.com  ca   (en at /, fr at /fr)
 *   shop-gb.[env.]bodi.com  gb
 * Environments: prod (no label), stage, qa.
 *
 * Rules, per link:
 *   1. Environment: a stage / qa link on prod, or a prod link on stage, gets the current
 *      environment's host. (www.stage.bodi.com/* <-> www.bodi.com/*)
 *   2. Store: a link to another region's store path (/, /products, /collections, /pages, /cart,
 *      /search, /blogs, /policies, /account, with an optional locale prefix) is moved to the
 *      current store, and given the current locale prefix (/fr). Other paths on those hosts
 *      (marketing pages like /us/en/..., /blog, /login) only get rule 1.
 *   3. Account: account.bodi.com and account.shop[-xx].[env.]bodi.com become the current
 *      store's account host (from snippets/account-profile-url.liquid).
 *
 * Skipped: links outside <main>, relative / mailto / non-BODi links, the locale switcher,
 * the redirect modal, and anything inside [data-keep-store-link].
 *
 * Config comes from Liquid via data attributes on this script's tag (see snippets/scripts.liquid):
 *   data-host, data-locale-root, data-account-url
 */
(function () {
  const script = document.currentScript;
  const config = script ? script.dataset : {};

  const currentHost = (config.host || window.location.hostname).toLowerCase();
  const localeRoot = (config.localeRoot || '/').replace(/\/$/, ''); // '' or '/fr'

  const ENV_LABELS = ['stage', 'qa'];
  const SKIP = '.locale-switcher, .redirect-modal, [data-keep-store-link]';
  // [locale prefix] then / or a Shopify store path. Everything else is treated as non-store.
  const STORE_PATH =
    /^(\/[a-z]{2}(?:-[a-z]{2})?)?(\/(?:products|collections|pages|cart|search|blogs|policies|account)(?:\/|$)|\/?$)/i;
  const SUBDOMAIN_BY_REGION = { us: 'www', ca: 'shop-ca', gb: 'shop-gb' };
  const REGION_BY_LABEL = { '': 'us', www: 'us', shop: 'us', 'shop-us': 'us', 'shop-ca': 'ca', 'shop-gb': 'gb' };

  // -> { account, region, env } for a BODi store / account host, or null for anything else
  function parseHost(host) {
    if (host !== 'bodi.com' && !host.endsWith('.bodi.com')) return null;

    let labels = host.split('.').slice(0, -2); // drop bodi.com
    const env = labels.find((label) => ENV_LABELS.includes(label)) || '';
    labels = labels.filter((label) => !ENV_LABELS.includes(label));

    const account = labels[0] === 'account';
    if (account) labels = labels.slice(1);
    if (labels.length > 1) return null; // faq., next.www., etc.

    const label = labels[0] || '';
    if (!(label in REGION_BY_LABEL)) return null;
    return { account, region: REGION_BY_LABEL[label], env };
  }

  function buildStoreHost(region, env) {
    return SUBDOMAIN_BY_REGION[region] + '.' + (env ? env + '.' : '') + 'bodi.com';
  }

  // same naming as snippets/account-profile-url.liquid: prod us is account.shop, other us envs shop-us
  function buildAccountHost(region, env) {
    const store = region === 'us' ? (env ? 'shop-us' : 'shop') : 'shop-' + region;
    return 'account.' + store + '.' + (env ? env + '.' : '') + 'bodi.com';
  }

  const current = parseHost(currentHost);
  if (!current || current.account) return; // myshopify.com, localhost, unknown hosts

  // Liquid's account url is trusted only when it belongs to this store and environment: on a
  // www host it falls back to settings.account_url, which can hold another environment's host.
  let accountHost = buildAccountHost(current.region, current.env);
  try {
    const provided = config.accountUrl ? new URL(config.accountUrl).hostname.toLowerCase() : '';
    const parsed = provided ? parseHost(provided) : null;
    if (parsed && parsed.account && parsed.region === current.region && parsed.env === current.env) {
      accountHost = provided;
    }
  } catch (e) {
    // keep the derived host
  }

  function correctUrl(url) {
    const target = parseHost(url.hostname.toLowerCase());
    if (!target) return false;

    // rule 3: account hosts follow the current store
    if (target.account) {
      if (!accountHost || url.hostname.toLowerCase() === accountHost) return false;
      url.hostname = accountHost;
      return true;
    }

    // rule 1: environment
    let region = target.region;
    let path = url.pathname;

    // rule 2: store (only for real store paths)
    const storePath = path.match(STORE_PATH);
    if (storePath && region !== current.region) {
      region = current.region;
      const rest = path.slice((storePath[1] || '').length); // drop the old locale prefix
      path = localeRoot + (rest === '' || rest === '/' ? (localeRoot ? '' : '/') : rest);
    }

    const host = buildStoreHost(region, current.env);
    if (host === url.hostname.toLowerCase() && path === url.pathname) return false;

    url.hostname = host;
    url.pathname = path;
    return true;
  }

  function fixLink(link) {
    const raw = link.getAttribute('href');
    // dataset holds the href we last handled, so a re-render that puts the old href back is re-checked
    if (!raw || link.dataset.storeLinkChecked === raw) return;
    if (!link.closest('main') || link.closest(SKIP)) return;

    link.dataset.storeLinkChecked = raw;
    if (!/^https?:\/\//i.test(raw)) return;

    let url;
    try {
      url = new URL(raw);
    } catch (e) {
      return;
    }

    if (!correctUrl(url)) return;

    const fixed = url.toString();
    link.setAttribute('href', fixed);
    link.dataset.storeLinkChecked = fixed;
    // rich text editors copy the URL into title="", so keep the tooltip in step
    if (link.getAttribute('title') === raw) link.setAttribute('title', fixed);
  }

  function scan(root) {
    if (root.nodeType !== 1 || !root.isConnected) return;
    if (root.matches('a[href]')) fixLink(root);
    root.querySelectorAll('a[href]').forEach(fixLink);
  }

  function init() {
    document.querySelectorAll('main').forEach(scan);

    // links added to <main> later, and hrefs reset by a section re-render
    let pending = [];
    let queued = false;

    function queue(node) {
      pending.push(node);
      if (queued) return;
      queued = true;

      requestAnimationFrame(() => {
        const nodes = pending;
        pending = [];
        queued = false;
        nodes.forEach(scan);
      });
    }

    new MutationObserver((mutations) => {
      mutations.forEach((m) => {
        if (m.type === 'attributes') queue(m.target);
        else m.addedNodes.forEach(queue);
      });
    }).observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['href'],
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
